import React, { forwardRef } from "react";
import { createPortal } from "react-dom";
import { NavLink } from "react-router-dom";
import Icon from "components/Icon";
import InitialsAvatar from "components/InitialsAvatar";

/**
 * O indicador de aba ativa: fundo atrás do ícone e do rótulo juntos, não só do ícone — a aba inteira
 * lê como selecionada. `bg-primary/10` é o mesmo "pressionado" do sino. É uma camada à parte, e não um
 * invólucro em volta dos dois, para o rótulo continuar sendo o último nó da aba e as iniciais do
 * avatar não se juntarem ao nome. O `-z-10` só funciona porque a aba tem `isolate`.
 */
function FundoAtivo({ aceso }) {
	return (
		<span
			aria-hidden="true"
			className={`absolute inset-x-1.5 inset-y-1 -z-10 rounded-2xl bg-primary/10 transition-opacity duration-200 ${
				aceso ? "opacity-100" : "opacity-0"
			}`}
		/>
	);
}

/**
 * A barra de abas do app nativo — a navegação principal de quem está logado, no rodapé, ao
 * alcance do polegar. Só a NavBar a monta, e só no app com sessão (ver `comAbas` lá).
 *
 * Painel · Buscar · Agenda · Menu, nesta ordem: a "casa" do app na primeira aba (é onde ele abre
 * depois do login) e a conta na última, onde os apps de referência põem o perfil. "Buscar" é de quem
 * está contratando: o cliente e a profissional no modo contratando (ver hooks/useModoConta). No modo
 * trabalhando ela sai, como sai a busca do modo anfitrião do Airbnb, e o sanduíche troca o item por
 * "Fale conosco". Enquanto o perfil não resolveu (`perfilEfetivo === null`) a aba fica de fora, em
 * vez de aparecer e sumir: é a mesma regra do item de destaque do menu.
 *
 * O ponto âmbar da aba da conta acende por dois motivos: cadastro pendente, ou o outro modo com
 * pedido esperando (`avisoOutroModo`). Os dois levam ao mesmo sanduíche, onde cada um tem seu item.
 *
 * A quarta aba não navega: abre o sanduíche de hoje, sem mudança de conteúdo. O rótulo é o primeiro
 * nome de quem está logado — é a aba da conta, e a inicial no círculo sozinha não diz de quem. "Menu"
 * fica só de reserva, para o token sem nome. Nome comprido é cortado com reticências para não
 * empurrar as outras abas. Ela usa o mesmo fundo de aba ativa das outras, aceso enquanto o menu está
 * aberto — um sinal só de "selecionado" nas quatro.
 *
 * Portal para o body pelo mesmo motivo dos painéis da NavBar: o backdrop-blur do <nav> vira
 * containing block de position:fixed, e a barra ficaria presa dentro do header.
 *
 * O h-[3.75rem] espelha --altura-barra-inferior em styles/index.css — mudar aqui é mudar lá.
 */
const BarraInferior = forwardRef(function BarraInferior(
	{ perfilEfetivo, firstName, userName, avatar, isMenuOpen, onToggleMenu, cadastroPendente, avisoOutroModo = false, botaoAvatarRef },
	ref
) {
	// Contorno em toda aba, cheio só naquela em que a pessoa está — o mesmo sinal do sanduíche.
	const abaClasse = ({ isActive }) =>
		`relative isolate flex min-w-0 flex-1 flex-col items-center justify-center gap-0.5 text-2xs font-medium transition-colors duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary ${
			isActive ? "text-primary" : "text-muted hover:text-primary"
		}`;

	const abas = [
		{ to: "/dashboard", icone: "monitoring", rotulo: "Painel" },
		...(perfilEfetivo === "usuario" ? [{ to: "/profissional", icone: "search", rotulo: "Buscar" }] : []),
		{ to: "/agenda", icone: "calendar_month", rotulo: "Agenda" },
	];

	return createPortal(
		<div className="md:hidden" ref={ref}>
			<nav
				aria-label="Navegação principal do app"
				className="fixed inset-x-0 bottom-0 z-30 border-t border-border bg-white/95 pb-[var(--safe-bottom)] backdrop-blur"
			>
				<div className="flex h-[3.75rem] items-stretch">
					{abas.map((aba) => (
						<NavLink key={aba.to} to={aba.to} className={abaClasse} onClick={() => isMenuOpen && onToggleMenu()}>
							{({ isActive }) => (
								<>
									<FundoAtivo aceso={isActive} />
									<span className="flex h-7 items-center justify-center">
										<Icon name={aba.icone} filled={isActive} className="h-6 w-6" />
									</span>
									{aba.rotulo}
								</>
							)}
						</NavLink>
					))}

					{/* O mesmo avatar (foto ou iniciais) do bloco de identidade do sanduíche, em 26px.
					    O ponto âmbar de pendência mora aqui, e não mais no hambúrguer: é a aba da conta.
					    A NavBar recebe este botão por ref para devolver o foco a ele quando o menu
					    fecha — o mesmo papel que o hambúrguer tem na web. */}
					<button
						ref={botaoAvatarRef}
						type="button"
						onClick={onToggleMenu}
						aria-controls="mobile-menu"
						aria-expanded={isMenuOpen}
						aria-label={isMenuOpen ? "Fechar menu" : "Abrir menu"}
						className={`relative isolate flex min-w-0 flex-1 flex-col items-center justify-center gap-0.5 text-2xs font-medium transition-colors duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary ${
							isMenuOpen ? "text-primary" : "text-muted hover:text-primary"
						}`}
					>
						<FundoAtivo aceso={isMenuOpen} />
						<span className="flex h-7 items-center justify-center">
							<span className="relative flex h-[26px] w-[26px] items-center justify-center rounded-full">
								{userName ? (
									<InitialsAvatar name={userName} avatar={avatar} className="h-[26px] w-[26px] rounded-full text-xs" />
								) : (
									<span className="flex h-[26px] w-[26px] items-center justify-center rounded-full bg-primary text-white">
										<Icon name="person" filled className="h-4 w-4" />
									</span>
								)}
								{(cadastroPendente || avisoOutroModo) && (
									<span aria-hidden="true" className="absolute -right-0.5 -top-0.5 h-2.5 w-2.5 rounded-full bg-amber-500 ring-2 ring-white" />
								)}
							</span>
						</span>
						<span className="max-w-full truncate px-1">{firstName || "Menu"}</span>
					</button>
				</div>
			</nav>
		</div>,
		document.body
	);
});

export default BarraInferior;
